/**
 * The playground: a brand picker and a chat box — the fastest way to answer
 * "what does it say if I ask it this?" without waiting for a real ticket.
 *
 * Three things the vendor's version does not have:
 *  1. The trace beside the reply — which layer decided: routing, conditions,
 *     tools, policy. When a reply is wrong you see WHERE it went wrong.
 *  2. Blocked drafts shown as blocked, with the rule that stopped them and
 *     what a customer would actually receive.
 *  3. Replay a real ticket by id against current config (the box below the
 *     composer) — the ten-second sanity check before a change goes near the
 *     eval suite.
 *
 * The banner is permanent chrome, not a dismissible toast: writes are always
 * simulated here, there is no toggle, and the API enforces it — the copy just
 * says so.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Paperclip, RotateCcw, Send, ShieldCheck, X } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { STILL_RUNNING_NOTE, findTicketByExternalId } from '../api/tickets';
import { useBrands } from '../api/admin';
import { isSuperadmin, useSession } from '../auth/session';
import { Badge, Button } from '../components/ui/primitives';
import { redactCardNumbers } from '../lib/card';
import { withBillingLinkStandIn } from '../lib/format';
import { readRunFacts } from '../lib/ticket/detail';
import { RunTraceSections } from '../components/trace/RunTraceSections';
import {
  buildTranscriptMarkdown,
  downloadMarkdown,
  pairSessionTurns,
  transcriptFilename,
  type SessionRunOutcome,
} from '../lib/playground/transcript';

interface TraceToolCall {
  id: string;
  tool: string;
  ok: boolean;
  latencyMs: number;
}

interface Trace {
  id: string;
  status: string;
  workflowSlug: string | null;
  variantId: string | null;
  draftReply: string | null;
  escalated: boolean;
  escalationReason: string | null;
  confidence: number | null;
  facts: unknown;
  tags: string[];
  toolCalls: TraceToolCall[];
}

interface PlaygroundResult {
  stage: 'intake' | 'agent';
  ticketId: string | null;
  /** The session's sequential ticket number (from 1000), minted on turn one. */
  ticketNumber?: number | null;
  runId: string | null;
  reason?: string;
  /** The agent stage's own account: 'skipped' = the pipeline could not run. */
  agent?: { status: 'drafted' | 'escalated' | 'skipped'; reason: string | null };
  trace: Trace | null;
}

interface Turn {
  message: string;
  result: PlaygroundResult | null;
  problem: string | null;
  /** Photos sent with this message, for the transcript view. */
  photoNames?: string[];
  /** True while the request died but the server may still be working. */
  recovering?: boolean;
  /** A reply loaded from a stored session rather than a live run. */
  restoredReply?: string | null;
  /** Why a restored turn has no reply — blocked draft, escalation. */
  restoredNote?: string | null;
  /** A restored draft a rule blocked — written, never delivered. */
  restoredBlockedDraft?: string | null;
}

/** A photo staged in the composer, already read and capped client-side. */
interface PendingPhoto {
  fileName: string;
  contentType: string;
  dataBase64: string;
}

const PHOTO_MAX_COUNT = 4;
const PHOTO_MAX_BYTES = 4 * 1024 * 1024;
const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

interface SessionSummary {
  sessionId: string;
  ticketNumber: number | null;
  brandSlug: string;
  brandName: string;
  openedAt: string;
  customerEmail: string | null;
  messages: number;
}

interface SessionDetail {
  sessionId: string;
  ticketNumber: number | null;
  brandSlug: string;
  customerEmail: string | null;
  messages: Array<{ role: string; body: string; createdAt: string }>;
  /** Per-turn run outcomes, oldest first — why a turn has no reply. */
  runOutcomes?: SessionRunOutcome[];
  trace: (Trace & { startedAt?: string }) | null;
}

const RECOVERY_POLL_MS = 4_000;
const RECOVERY_MAX_MS = 5 * 60_000;

function newSessionId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function PlaygroundPage() {
  const brands = useBrands();
  const session = useSession();
  const [brandSlug, setBrandSlug] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [sessionId, setSessionId] = useState(newSessionId);
  // The reporting reference: minted server-side on the session's first turn,
  // shown in the toolbar so the tester can quote "ticket 1042" in feedback.
  const [ticketNumber, setTicketNumber] = useState<number | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [message, setMessage] = useState('');
  // Photos staged for the next message — the damaged/wrong-item flows are
  // gated on them, and ops tests those flows here.
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [photoProblem, setPhotoProblem] = useState<string | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  // The brands this caller may playground: superadmin all of them, otherwise
  // the ones where they hold an operational role. Mirrors the API's own
  // per-brand check — the picker is a courtesy, the 403 is the rule.
  const administrable = useMemo(() => {
    const rows = brands.data ?? [];
    if (!session.data) return [];
    if (isSuperadmin(session.data)) return rows;
    const playable = new Set(
      session.data.memberships
        .filter((m) => m.role === 'ops_agent' || m.role === 'brand_admin')
        .map((m) => m.brandId),
    );
    return rows.filter((brand) => playable.has(brand.id));
  }, [brands.data, session.data]);
  const selected = brandSlug || administrable[0]?.slug || '';

  const run = useMutation({
    mutationFn: ({ text, attachments }: { text: string; attachments: PendingPhoto[] }) =>
      apiPost<PlaygroundResult>('/api/playground', {
        brandSlug: selected,
        message: text,
        sessionId,
        ...(customerEmail.trim() ? { customerEmail: customerEmail.trim() } : {}),
        ...(attachments.length > 0 ? { attachments } : {}),
      }),
    onSuccess: (result) => {
      if (result.ticketNumber != null) setTicketNumber(result.ticketNumber);
      setTurns((current) => {
        const next = [...current];
        next[next.length - 1] = { ...next[next.length - 1]!, result };
        return next;
      });
    },
    onError: (error: unknown, { text }) => {
      // A 4xx is a real refusal and is shown as one. Anything else — a proxy
      // timeout, a dropped connection — usually means the request died while
      // the RUN kept going server-side (a heavy turn can outlive nginx; the
      // exact failure replay already recovers from). Poll the stored session
      // until the run stops being `running` instead of stranding the tester.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setTurns((current) => {
          const next = [...current];
          next[next.length - 1] = { ...next[next.length - 1]!, problem: error.message };
          return next;
        });
        return;
      }
      setTurns((current) => {
        const next = [...current];
        next[next.length - 1] = { ...next[next.length - 1]!, recovering: true };
        return next;
      });
      void recoverTurn(text);
    },
  });

  const recoverTurn = async (sentText: string) => {
    const sentAt = Date.now();
    const finish = (patch: Partial<Turn>) =>
      setTurns((current) => {
        const index = current.findIndex((turn) => turn.message === sentText && turn.recovering);
        if (index < 0) return current;
        const next = [...current];
        next[index] = { ...next[index]!, recovering: false, ...patch };
        return next;
      });

    while (Date.now() - sentAt < RECOVERY_MAX_MS) {
      await new Promise((resolve) => setTimeout(resolve, RECOVERY_POLL_MS));
      try {
        const detail = await apiGet<SessionDetail>(`/api/playground/sessions/${sessionId}`);
        const trace = detail.trace;
        // Two guards, both learned the hard way. The latest run must be THIS
        // turn's — until intake has stored our message, the newest trace is
        // the previous turn's and its finished draft would render as this
        // turn's reply. And "not running" is NOT the finished test: intake
        // marks the run `drafted` before the agent stage even starts, and
        // polling on status grabbed that snapshot — no draft, no reason — and
        // rendered it as a phantom escalation. Finished means the pipeline
        // produced its outcome: a draft, or a real escalation.
        const lastCustomerMessage = [...detail.messages]
          .reverse()
          .find((entry) => entry.role === 'customer');
        const isThisTurn = lastCustomerMessage?.body === sentText;
        if (isThisTurn && trace && (trace.escalated || trace.draftReply !== null)) {
          if (detail.ticketNumber != null) setTicketNumber(detail.ticketNumber);
          finish({
            result: { stage: 'agent', ticketId: null, runId: trace.id, trace },
          });
          return;
        }
      } catch {
        // The ticket may not exist yet if intake itself is still running, and
        // a transient fetch failure is exactly what we are waiting out.
      }
    }
    finish({
      problem:
        'The reply did not arrive within five minutes. The run may still land — pick this ' +
        'session from “Load session” in a moment to check.',
    });
  };

  const ask = () => {
    // The card boundary, browser-side: intake redacts again on the server,
    // but the turn bubble and the .md export render THIS text, and the raw
    // number should not ride the request either. What you see after sending
    // is exactly what the system kept.
    const text = redactCardNumbers(message.trim()).text;
    const attachments = photos;
    // A photo alone is a message (ops #1058) — customers answer "send a
    // photo" with just the photo, and this composer has to test that turn.
    if ((!text && attachments.length === 0) || run.isPending) return;
    setTurns((current) => [
      ...current,
      {
        message: text || '(photo only)',
        result: null,
        problem: null,
        ...(attachments.length > 0 ? { photoNames: attachments.map((p) => p.fileName) } : {}),
      },
    ]);
    setMessage('');
    setPhotos([]);
    setPhotoProblem(null);
    run.mutate({ text, attachments });
  };

  const addPhotos = async (files: FileList | null) => {
    if (!files) return;
    setPhotoProblem(null);
    const staged: PendingPhoto[] = [];
    for (const file of Array.from(files)) {
      if (!PHOTO_TYPES.has(file.type)) {
        setPhotoProblem(`"${file.name}" is not a JPEG/PNG/WebP/GIF photo.`);
        continue;
      }
      if (file.size > PHOTO_MAX_BYTES) {
        setPhotoProblem(`"${file.name}" is over the 4MB cap.`);
        continue;
      }
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
      }).catch(() => null);
      if (!dataUrl) {
        setPhotoProblem(`"${file.name}" could not be read.`);
        continue;
      }
      staged.push({
        fileName: file.name,
        contentType: file.type,
        dataBase64: dataUrl.slice(dataUrl.indexOf(',') + 1),
      });
    }
    setPhotos((current) => {
      const next = [...current, ...staged];
      if (next.length > PHOTO_MAX_COUNT) {
        setPhotoProblem(`At most ${PHOTO_MAX_COUNT} photos per message.`);
      }
      return next.slice(0, PHOTO_MAX_COUNT);
    });
  };

  const reset = () => {
    setSessionId(newSessionId());
    setTicketNumber(null);
    setTurns([]);
  };

  // Past sessions — always persisted, until now invisible. Ops tests here and
  // their feedback needs the conversation it refers to still on screen.
  const sessions = useQuery({
    queryKey: ['playground-sessions'],
    queryFn: () => apiGet<{ sessions: SessionSummary[] }>('/api/playground/sessions'),
  });

  // Opened from the Playground log page: /playground?session=<id> restores
  // that session as if picked from the Load-session select. Once only —
  // the ref guards against effect re-runs restoring over live typing.
  const [searchParams] = useSearchParams();
  const restoredFromUrl = useRef(false);
  useEffect(() => {
    const requested = searchParams.get('session');
    if (!requested || restoredFromUrl.current) return;
    restoredFromUrl.current = true;
    void restore(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const saveTranscript = () => {
    const brandName =
      administrable.find((brand) => brand.slug === selected)?.name ?? selected ?? 'unknown';
    const meta = {
      sessionId,
      ticketNumber,
      brandName,
      customerEmail: customerEmail.trim() || null,
    };
    downloadMarkdown(
      transcriptFilename(meta),
      buildTranscriptMarkdown(
        meta,
        turns.map((turn) => {
          const trace = turn.result?.trace ?? null;
          // A kept draft on an escalated run never reached the customer —
          // exporting it as "Zoye drafts:" would show ops a reply that was
          // in fact blocked (#1051's export said "no reply was stored" for
          // exactly such a turn).
          const blockedDraft =
            (trace?.escalated && trace.draftReply ? trace.draftReply : null) ??
            turn.restoredBlockedDraft ??
            null;
          return {
            message: turn.message,
            reply: blockedDraft ? null : (trace?.draftReply ?? turn.restoredReply ?? null),
            blockedDraft,
            note: turn.problem
              ? turn.problem
              : trace?.escalated
                ? `escalated: ${trace.escalationReason ?? 'no reason recorded'}`
                : turn.recovering
                  ? 'still running when saved'
                  : (turn.restoredNote ?? null),
          };
        }),
      ),
    );
  };

  const restore = async (id: string) => {
    const detail = await apiGet<SessionDetail>(`/api/playground/sessions/${id}`);
    setSessionId(detail.sessionId);
    setTicketNumber(detail.ticketNumber);
    setBrandSlug(detail.brandSlug);
    setCustomerEmail(detail.customerEmail ?? '');
    // Pair the stored transcript back into turns: each customer message opens
    // one, the reply that followed it rides along, and the run outcomes say
    // why a turn is silent — a blocked draft renders AS a blocked draft, not
    // as "no reply was stored" (ops read #1051's silence as neglect). Sending
    // again CONTINUES this session — same id, same synthetic ticket, ladder
    // state intact.
    const restored: Turn[] = pairSessionTurns(detail.messages, detail.runOutcomes).map((turn) => ({
      message: turn.message,
      result: null,
      problem: null,
      restoredReply: turn.reply,
      restoredNote: turn.reply ? null : (turn.note ?? null),
      restoredBlockedDraft: turn.blockedDraft ?? null,
    }));
    // The session stored its latest run's trace all along — the detail
    // endpoint returns it. Ride it on the last turn so the trace panel shows
    // what was called and established, exactly as it did live; without this a
    // reopened session rendered the empty placeholder.
    if (detail.trace && restored.length > 0) {
      restored[restored.length - 1] = {
        ...restored[restored.length - 1]!,
        result: {
          stage: 'agent',
          ticketId: null,
          ticketNumber: detail.ticketNumber,
          runId: detail.trace.id,
          trace: detail.trace,
        },
      };
    }
    setTurns(restored);
  };

  const lastTrace = [...turns].reverse().find((turn) => turn.result?.trace)?.result?.trace ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line bg-surface px-4 py-2.5">
        <label className="flex items-center gap-2 text-xs text-ink-500">
          Brand
          <select
            value={selected}
            onChange={(event) => {
              setBrandSlug(event.target.value);
              reset();
            }}
            className="rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
          >
            {administrable.map((brand) => (
              <option key={brand.id} value={brand.slug}>
                {brand.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-xs text-ink-500">
          Act as
          <input
            value={customerEmail}
            onChange={(event) => setCustomerEmail(event.target.value)}
            placeholder="customer email (optional)"
            className="w-56 rounded border border-line bg-surface px-2 py-1.5 font-mono text-xs text-ink-900"
          />
        </label>

        {ticketNumber != null ? (
          <span
            title="Quote this number when reporting feedback on this session"
            className="rounded bg-surface-sunken px-2 py-1 font-mono text-xs font-semibold text-ink-900 shadow-hairline"
          >
            ticket #{ticketNumber}
          </span>
        ) : null}

        <Button size="sm" variant="ghost" onClick={reset}>
          <RotateCcw size={13} strokeWidth={2} aria-hidden />
          New session
        </Button>

        <Button size="sm" variant="ghost" onClick={saveTranscript} disabled={turns.length === 0}>
          <Download size={13} strokeWidth={2} aria-hidden />
          Save as .md
        </Button>

        <label className="flex items-center gap-2 text-xs text-ink-500">
          Load session
          <select
            value=""
            onChange={(event) => {
              if (event.target.value) void restore(event.target.value);
            }}
            aria-label="Load a previous playground session"
            className="max-w-64 rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-900"
          >
            <option value="">
              {sessions.data ? `${sessions.data.sessions.length} saved…` : 'loading…'}
            </option>
            {(sessions.data?.sessions ?? []).map((session) => (
              <option key={session.sessionId} value={session.sessionId}>
                {session.ticketNumber != null ? `#${session.ticketNumber} · ` : ''}
                {session.brandName} · {new Date(session.openedAt).toLocaleString()} ·{' '}
                {session.messages} msg{session.customerEmail ? ` · ${session.customerEmail}` : ''}
              </option>
            ))}
          </select>
        </label>

        <span className="ml-auto flex items-center gap-1.5 rounded bg-info-100 px-2.5 py-1 text-xs font-semibold text-info-700">
          <ShieldCheck size={13} strokeWidth={2} aria-hidden />
          Writes are always simulated — no toggle exists
        </span>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* chat */}
        <div className="flex min-w-0 flex-1 flex-col bg-paper">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            {turns.length === 0 ? (
              <p className="py-10 text-center text-sm text-ink-400">
                Ask {selected ? `${selected}'s` : 'the'} Zoye anything. With a customer email set,
                it reads that customer&rsquo;s real orders — reads only.
              </p>
            ) : null}

            {turns.map((turn, index) => (
              <div key={index} className="space-y-3">
                <div className="max-w-[560px] rounded border border-line bg-surface px-3 py-2 text-sm">
                  <p className="mb-1 text-2xs uppercase tracking-wide text-ink-400">
                    you, as the customer
                  </p>
                  <p className="whitespace-pre-wrap text-ink-700">{turn.message}</p>
                  {turn.photoNames?.length ? (
                    <p className="mt-1 flex items-center gap-1 text-2xs text-ink-400">
                      <Paperclip size={11} strokeWidth={2} aria-hidden />
                      {turn.photoNames.join(', ')}
                    </p>
                  ) : null}
                </div>

                {turn.problem ? (
                  <p className="text-sm text-stop-700">{turn.problem}</p>
                ) : turn.result ? (
                  <TurnReply result={turn.result} />
                ) : turn.restoredReply ? (
                  <div className="max-w-[560px] rounded border border-accent-100 bg-accent-50 px-3 py-2 text-sm">
                    <p className="mb-1 flex items-center gap-2 text-2xs uppercase tracking-wide text-ink-400">
                      Zoye drafted <Badge tone="info">simulated session</Badge>
                    </p>
                    <p className="whitespace-pre-wrap text-ink-700">
                      <Linkified text={turn.restoredReply} />
                    </p>
                  </div>
                ) : turn.recovering ? (
                  <p className="text-sm text-ink-400">
                    The request timed out but the run keeps going on the server — waiting for the
                    reply…
                  </p>
                ) : turn.restoredBlockedDraft ? (
                  <div className="max-w-[560px] rounded border border-stop-700 bg-stop-100 px-3 py-2 text-sm">
                    <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-stop-700">
                      Draft blocked — never sent
                    </p>
                    <p className="whitespace-pre-wrap text-ink-500 line-through">
                      {turn.restoredBlockedDraft}
                    </p>
                    <p className="mt-1.5 text-xs text-stop-700">{turn.restoredNote}</p>
                  </div>
                ) : turn.restoredReply === null && 'restoredReply' in turn ? (
                  <p className="text-sm text-ink-400">
                    {turn.restoredNote ?? 'No reply was stored for this message.'}
                  </p>
                ) : (
                  <p className="text-sm text-ink-400">Running the pipeline…</p>
                )}
              </div>
            ))}
          </div>

          <div className="shrink-0 border-t border-line bg-surface px-4 py-3">
            {photos.length > 0 || photoProblem ? (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {photos.map((photo, index) => (
                  <span
                    key={`${photo.fileName}-${index}`}
                    className="flex items-center gap-1 rounded border border-line bg-canvas px-2 py-0.5 text-xs text-ink-700"
                  >
                    <Paperclip size={11} strokeWidth={2} aria-hidden />
                    {photo.fileName}
                    <button
                      type="button"
                      aria-label={`Remove ${photo.fileName}`}
                      className="text-ink-400 hover:text-stop-700"
                      onClick={() => setPhotos((current) => current.filter((_, i) => i !== index))}
                    >
                      <X size={12} strokeWidth={2} aria-hidden />
                    </button>
                  </span>
                ))}
                {photoProblem ? <span className="text-xs text-stop-700">{photoProblem}</span> : null}
              </div>
            ) : null}
            <div className="flex gap-2">
              <input
                ref={photoInput}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                hidden
                onChange={(event) => {
                  void addPhotos(event.target.files);
                  event.target.value = '';
                }}
              />
              <Button
                variant="ghost"
                aria-label="Attach photos"
                title="Attach photos (damaged/wrong-item flows are gated on them)"
                disabled={run.isPending || photos.length >= PHOTO_MAX_COUNT}
                onClick={() => photoInput.current?.click()}
              >
                <Paperclip size={14} strokeWidth={2} aria-hidden />
              </Button>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    ask();
                  }
                }}
                rows={2}
                placeholder="Ask as a customer… (Enter sends, Shift+Enter for a new line)"
                aria-label="Playground message"
                className="min-w-0 flex-1 rounded border border-line bg-surface px-3 py-2 text-sm text-ink-900"
              />
              <Button
                variant="primary"
                disabled={(!message.trim() && photos.length === 0) || !selected || run.isPending}
                onClick={ask}
              >
                <Send size={14} strokeWidth={2} aria-hidden />
                {run.isPending ? 'Running…' : 'Send'}
              </Button>
            </div>
            {/* Replay touches real Zendesk tickets and keeps its superadmin
                gate — hidden rather than shown-and-refused. */}
            {session.data && isSuperadmin(session.data) ? <ReplayBox /> : null}
          </div>
        </div>

        {/* trace */}
        <aside className="hidden w-96 shrink-0 overflow-y-auto border-l border-line bg-surface xl:block">
          {lastTrace ? (
            <TraceView trace={lastTrace} />
          ) : (
            <p className="px-4 py-8 text-center text-xs text-ink-400">
              The trace appears here — which layer decided: routing, conditions, tools, policy.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * A draft with its web addresses clickable, as Zendesk makes them in the
 * email the customer actually receives (ops #1135: the playground showed
 * them as dead text, which read as "no hyperlinks").
 */
function Linkified({ text }: { text: string }) {
  // The customer's secure billing link is only fetched when a reply is really
  // SENT; a playground draft never is, so it shows where the link would go.
  const shown = withBillingLinkStandIn(text);
  const parts = shown.split(/(https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((part, index) => {
        if (!/^https?:\/\//.test(part)) return part;
        // Sentence punctuation after an address is not part of it.
        const url = part.replace(/[.,;:!?)]+$/, '');
        const tail = part.slice(url.length);
        return (
          <span key={index}>
            <a href={url} target="_blank" rel="noreferrer noopener" className="text-accent-700 underline">
              {url}
            </a>
            {tail}
          </span>
        );
      })}
    </>
  );
}

/** The reply — or, more informatively, why there is none. */
function TurnReply({ result }: { result: PlaygroundResult }) {
  const trace = result.trace;
  const facts = readRunFacts(trace?.facts);

  if (!trace) {
    return (
      <p className="text-sm text-ink-500">
        Intake stopped before a run existed: {result.reason ?? 'unusable payload'}.
      </p>
    );
  }

  // A pipeline that COULD NOT run is a configuration problem, and rendering
  // it as an escalation would read as the bot deciding to give up. It did not
  // decide anything — nothing ran. An escalation INTAKE decided (the refund
  // chase, a request for a person) also skips the agent stage, but that IS a
  // decision — ops #1123 saw its handoff reply hidden behind this banner.
  if ((result.agent?.status === 'skipped' && !trace.escalated) || trace.status === 'running') {
    return (
      <div className="max-w-[560px] rounded border border-hold-700 bg-hold-100 px-3 py-2 text-sm">
        <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-hold-700">
          The pipeline could not run — configuration, not a decision
        </p>
        <p className="text-hold-700">
          {result.agent?.reason ?? 'The agent stage never finished.'}
          {result.agent?.reason?.includes('no model available')
            ? ' Check Admin → Models for the role, and that its provider API key (ANTHROPIC_API_KEY / GOOGLE_AI_API_KEY / OPENAI_API_KEY) is set on the server.'
            : ''}
        </p>
      </div>
    );
  }

  // Only a BLOCK-severity violation stops the reply. A flag-severity one is
  // recorded on the trace and the draft still goes out — rendering it as
  // "blocked / escalation to a human" claimed an escalation that never
  // happened (#1037: offer_not_made, a flag, wore the red banner while the
  // customer would in fact have received the cancellation confirmation).
  const blockingViolations = facts.violations.filter((v) => v.severity === 'block');
  if (blockingViolations.length > 0) {
    // The most informative object on the screen, so it is the loudest: what
    // the model wrote, struck through, and what would actually happen.
    return (
      <div className="max-w-[560px] rounded border border-stop-700 bg-stop-100 px-3 py-2 text-sm">
        <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-stop-700">
          Draft blocked — rule: {blockingViolations[0]?.ruleId.replace(/_/g, ' ')}
        </p>
        {trace.draftReply ? (
          <p className="whitespace-pre-wrap text-ink-500 line-through">{trace.draftReply}</p>
        ) : null}
        <p className="mt-1.5 text-xs text-stop-700">
          What a customer would get: escalation to a human.
          {trace.tags.length > 0 ? ` Tagged ${trace.tags.join(', ')}.` : ''}
        </p>
      </div>
    );
  }

  // The one escalated run that DID reply: the handoff acknowledgment for
  // "I want to talk to a human" (Deivydas 2026-09-16) — the customer is told
  // a teammate is taking over, and the agent goes silent from there.
  const handoff =
    (trace.facts as Record<string, unknown> | null)?.handoffAcknowledgment === true;
  if (trace.escalated && handoff && trace.draftReply) {
    return (
      <div className="max-w-[560px] rounded border border-accent-100 bg-accent-50 px-3 py-2 text-sm">
        <p className="mb-1 text-2xs uppercase tracking-wide text-ink-400">Zoye replies</p>
        <p className="whitespace-pre-wrap text-ink-700"><Linkified text={trace.draftReply} /></p>
        <p className="mt-1.5 text-xs text-hold-700">
          Handed to a human — the agent stays silent on this ticket from here.
        </p>
      </div>
    );
  }

  if (trace.escalated || !trace.draftReply) {
    return (
      <div className="max-w-[560px] rounded border border-line bg-surface px-3 py-2 text-sm">
        <p className="mb-1 text-2xs uppercase tracking-wide text-hold-700">
          Escalated — a person answers this one
        </p>
        <p className="text-ink-700">{trace.escalationReason ?? 'No reason recorded.'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-[560px] rounded border border-accent-100 bg-accent-50 px-3 py-2 text-sm">
      <p className="mb-1 text-2xs uppercase tracking-wide text-ink-400">Zoye drafts</p>
      <p className="whitespace-pre-wrap text-ink-700"><Linkified text={trace.draftReply} /></p>
      {facts.violations.length > 0 ? (
        // Flag-severity findings: the reply went out as written, and the
        // trace wants a second look at it.
        <p className="mt-1.5 text-xs text-hold-700">
          Flagged (sent anyway): {facts.violations.map((v) => v.ruleId.replace(/_/g, ' ')).join(', ')}
        </p>
      ) : null}
    </div>
  );
}

function TraceView({ trace }: { trace: Trace }) {
  const facts = readRunFacts(trace.facts);

  return (
    <div className="text-xs">
      <div className="border-b border-line px-4 py-2.5 font-semibold text-ink-900">Trace</div>

      <RunTraceSections
        run={{
          workflowSlug: trace.workflowSlug,
          variantId: trace.variantId,
          confidence: trace.confidence,
          tags: trace.tags,
          facts: trace.facts,
          toolCalls: trace.toolCalls,
        }}
      />

      {facts.proposedWrites.length > 0 ? (
        <section className="px-4 py-3">
          <div className="eyebrow mb-1.5">Proposed writes — simulated</div>
          <ul className="space-y-1">
            {facts.proposedWrites.map((write, index) => (
              <li key={index} className="flex items-center justify-between">
                <span className="font-mono text-ink-700">{write.tool}</span>
                <Badge tone="info">simulated</Badge>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Replay a real ticket by id against current config. */
function ReplayBox() {
  const navigate = useNavigate();
  const brands = useBrands();
  const [ticketId, setTicketId] = useState('');
  const [brandSlug, setBrandSlug] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const administrable = useMemo(
    () => (brands.data ?? []).filter((brand) => brand.canAdminister),
    [brands.data],
  );
  const selected = brandSlug || administrable[0]?.slug || '';

  const replay = useMutation({
    mutationFn: () =>
      apiPost<{ ticketId: string | null; reason?: string }>('/api/tickets/replay', {
        ticketId: ticketId.trim(),
        brandSlug: selected,
      }),
    onSuccess: (result) => {
      if (result.ticketId) {
        navigate(`/tickets/${result.ticketId}`);
        return;
      }
      setNote(result.reason ?? 'Intake stopped before the ticket could be created.');
    },
    onError: async (error: unknown) => {
      // A timeout is the response dying, not the run: the server finishes
      // anyway. If the ticket row is already there, open it as if nothing
      // had gone wrong.
      const found = await findTicketByExternalId(ticketId.trim());
      if (found) {
        navigate(`/tickets/${found}`);
        return;
      }
      setNote(
        error instanceof ApiError && error.message !== ''
          ? error.message
          : STILL_RUNNING_NOTE,
      );
    },
  });

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-2xs text-ink-400">
      <span>Or replay a real ticket against current config:</span>
      <input
        value={ticketId}
        onChange={(event) => setTicketId(event.target.value)}
        placeholder="Zendesk id"
        aria-label="Zendesk ticket id to replay"
        className="w-28 rounded border border-line bg-surface px-2 py-1 font-mono text-2xs text-ink-900"
      />
      <select
        value={selected}
        onChange={(event) => setBrandSlug(event.target.value)}
        aria-label="Brand for the replay"
        className="rounded border border-line bg-surface px-1.5 py-1 text-2xs text-ink-900"
      >
        {administrable.map((brand) => (
          <option key={brand.id} value={brand.slug}>
            {brand.name}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        variant="ghost"
        disabled={!ticketId.trim() || !selected || replay.isPending}
        onClick={() => replay.mutate()}
      >
        {replay.isPending ? 'Running…' : 'Replay'}
      </Button>
      {note ? <span className="text-stop-700">{note}</span> : null}
    </div>
  );
}
